/**
 * What the paste window holds while open: the header (what, from where),
 * the countries it goes to (v2, 26/10/2026 — several at once, a campaign
 * group in each), the shown country's group and the server's review of
 * its request — asked again on every choice, a thin bar sweeping the
 * header while an answer loads — and the footer with Paste. 27/10/2026: one
 * calm column; each country's review mounted on its own (its card starts over).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { CopyPlus } from 'lucide-react';

import { Button, DrawerBody, DrawerDescription, DrawerHeader, DrawerTitle, Skeleton } from '@/components/ui';
import { type GroupEntry, middleTruncate } from '@/features/drop-generator/components/campaign/campaign-scope';
import type { GroupCopyClip } from '@/features/drop-generator/components/group-copy/clipboard/group-copy-clipboard';
import { useCountryNameOf } from '@/features/drop-generator/components/group-copy/clipboard/use-country-name';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

import { type TargetReview, useTargetReviews } from './model/targets/target-reviews';
import { usePasteTargets } from './model/targets/use-paste-targets';
import { defaultTarget } from './model/paste-summary';
import { usePasteAll } from './model/use-paste-action';
import { PasteFooter } from './PasteFooter';
import { ReviewContent } from './ReviewContent';
import { TargetInto } from './targets/TargetInto';
import { TargetTabs } from './targets/TargetTabs';

export interface PasteDialogProps {
  clip: GroupCopyClip;
  countryKey: string;
  countryName: string;
  /** This country's groups the copy can land in (never the copied group itself). */
  groups: GroupEntry[];
  openKey: string | null;
  /** Every country pasted: the board's group to open (null = this country was not one of them). */
  onPasted: (groupKey: string | null) => void;
}

const PasteHeader: FC<{ clip: GroupCopyClip; busy: boolean }> = ({ clip, busy }) => (
  <DrawerHeader className="relative gap-0 px-5 py-3.5">
    <div className="flex min-w-0 items-center gap-3 pr-8">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10" aria-hidden>
        <CopyPlus className="h-4 w-4 text-primary" />
      </span>
      <div className="min-w-0">
        <DrawerTitle className="truncate text-[15px] font-semibold" title={clip.label}>Paste {middleTruncate(clip.label, 56)}</DrawerTitle>
        <p className="truncate text-xs text-muted-foreground">from {countryToFlagEmoji(clip.countryName)} {clip.countryName}</p>
      </div>
    </div>
    <DrawerDescription className="sr-only">The copied campaign group&apos;s master config and containers, each gateway, list and campaign on its match in every country it is pasted into.</DrawerDescription>
    {busy ? (
      <span className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 overflow-hidden" aria-hidden>
        <span className="block h-full w-1/3 animate-dg-indeterminate bg-primary/70" />
      </span>
    ) : null}
  </DrawerHeader>
);

/** While the first answer loads: the card and a few folded rows, shimmering. */
const Reading: FC = () => (
  <div className="flex flex-col gap-3" aria-busy="true">
    <p className="sr-only">Finding each gateway, list and campaign&apos;s match…</p>
    <Skeleton className="h-[74px] rounded-xl" />
    <div className="flex flex-col gap-2 rounded-xl border border-border/70 p-3">
      {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-7" />)}
    </div>
  </div>
);

const ReviewState: FC<{ t: TargetReview; children: (review: NonNullable<TargetReview['review']>) => ReactNode }> = ({ t, children }) => {
  const { query } = t;
  if (!t.target.groupKey) return <p className="rounded-xl border border-dashed border-border px-5 py-6 text-center text-sm text-muted-foreground">Pick the campaign group the copy lands in.</p>;
  if (t.review) return <>{children(t.review)}</>;
  if (query.isError)
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/40 px-5 py-4 text-sm text-destructive">
        Could not read what comes over — {getApiErrorMessage(query.error)}
        <Button type="button" size="sm" variant="outline" onClick={() => query.refetch()}>Retry</Button>
      </div>
    );
  return <Reading />;
};

export const PasteDialogBody: FC<PasteDialogProps & { onClose: () => void }> = ({ clip, countryKey, groups, openKey, onPasted, onClose }) => {
  const nameOf = useCountryNameOf();
  const t = usePasteTargets({ countryKey, groupKey: defaultTarget(groups, openKey, clip.label) });
  const all = useTargetReviews(clip, t.targets);
  const shown = all.find((e) => e.target.id === t.active?.id) ?? all[0];
  const canPaste = useAuth().hasPermission('drop_generator.manage');
  const paste = usePasteAll({
    nameOf,
    onPartial: t.remove,
    onDone: (done) => onPasted(done.find((d) => d.countryKey === countryKey)?.groupKey ?? null),
  });
  return (
    <>
      <PasteHeader clip={clip} busy={all.some((e) => e.query.isFetching)} />
      <DrawerBody className="flex min-h-0 flex-col gap-4 px-5 pb-5 pt-1">
        <TargetTabs all={all} activeId={shown?.target.id ?? ''} nameOf={nameOf} onActive={t.setActive} onRemove={(id) => t.remove([id])} onAdd={t.add} />
        {shown ? (
          <>
            <TargetInto clip={clip} countryKey={shown.target.countryKey} countryName={nameOf(shown.target.countryKey)} boardGroups={shown.target.countryKey === countryKey ? groups : undefined} groupKey={shown.target.groupKey} onPick={(key) => t.setGroup(shown.target.id, key)} />
            <ReviewState t={shown}>{(review) => <ReviewContent key={`${shown.target.id}|${shown.target.groupKey}`} review={review} choices={shown.target.choices} onChoices={(c) => t.setChoices(shown.target.id, c)} />}</ReviewState>
          </>
        ) : null}
      </DrawerBody>
      <PasteFooter all={all} nameOf={nameOf} state={{ pasting: paste.pasting, canPaste }} onCancel={onClose} onPaste={() => paste.run(all)} />
    </>
  );
};
